import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ContactsOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  LockOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, MenuProps } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { isUserDeactivated } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { User } from '@totem/components/users/container/types';
import { isNotNull } from '@totem/utilities/common';
import { USERS_ENDPOINT } from '@totem/utilities/endpoints';

interface ImpersonationResponse {
  access_token: string;
}

export interface Props {
  user: User;
  onAction: (action: string, user: User) => void;
}

const UserActionMenu = ({ user, onAction }: Props) => {
  const { userProfile, setApiToken, getApiToken } =
    useContext(UserProfileContext);
  const navigate = useNavigate();
  const isActiveUser = user.organization?.isActive;
  const isDeactivatedUser = isUserDeactivated(user);

  const getActionMenuItems = (): MenuProps['items'] => {
    if (permissions.organizationalAdministrator.hasPermissions(userProfile)) {
      if (isActiveUser) {
        const menuItems: MenuProps['items'] = [
          {
            key: 'view_profile',
            label: 'View Profile',
            title: 'View Profile',
            icon: <ContactsOutlined />,
          },
          {
            key: 'edit_user',
            label: (
              <IBTooltip
                content={permissions.organizationalAdministrator.tooltip}
              >
                Edit User
              </IBTooltip>
            ),
            title: 'Edit User',
            icon: <EditOutlined />,
          },
          {
            key: 'security_groups',
            label: (
              <IBTooltip
                content={permissions.organizationalAdministrator.tooltip}
              >
                Security Groups
              </IBTooltip>
            ),
            title: 'Security Groups',
            icon: <LockOutlined />,
          },
          {
            key: 'data_permissions',
            label: (
              <IBTooltip
                content={permissions.organizationalAdministrator.tooltip}
              >
                Data Permissions
              </IBTooltip>
            ),
            title: 'Data Permissions',
            icon: <LockOutlined />,
          },
        ];

        if (
          permissions.internalSystemAdministratorIBOrg.hasPermissions(
            userProfile,
          )
        ) {
          menuItems.push({
            key: 'remove_ib_user',
            label: (
              <IBTooltip
                content={permissions.internalSystemAdministratorIBOrg.tooltip}
              >
                Remove User
              </IBTooltip>
            ),
            title: 'Remove User',
            icon: <DeleteOutlined />,
          });
        } else {
          menuItems.push({
            key: 'remove_user',
            label: 'Remove User',
            title: 'Remove User',
            icon: <DeleteOutlined />,
          });
        }
        if (
          permissions.internalSystemAdministrator.hasPermissions(userProfile)
        ) {
          menuItems.push({
            key: 'impersonate',
            label: (
              <IBTooltip
                content={permissions.internalSystemAdministrator.tooltip}
              >
                Impersonate
              </IBTooltip>
            ),
            title: 'Impersonate',
            icon: <UserSwitchOutlined />,
          });
        }

        return menuItems;
      }

      const menuItems: MenuProps['items'] = [];
      if (isDeactivatedUser) {
        menuItems.push({
          key: 'view_details',
          label: 'View Details',
          title: 'View Details',
          icon: <ContactsOutlined />,
        });
      }

      if (permissions.organizationalAdministrator.hasPermissions(userProfile)) {
        menuItems.push({
          key: 'grant_access',
          label: (
            <IBTooltip
              content={permissions.internalSystemAdministrator.tooltip}
            >
              Grant Access
            </IBTooltip>
          ),
          title: 'Grant Access',
          icon: <UserAddOutlined />,
        });
      }

      return menuItems;
    }

    return [];
  };

  const handleImpersonation = useCallback(
    async (impUser: User) => {
      const payload = {
        userId: impUser.id,
      };

      fetch(`${USERS_ENDPOINT}/impersonate/${impUser.id}`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((result: ImpersonationResponse) => {
          if (
            isNotNull(result) &&
            isNotNull(result.access_token) &&
            result.access_token !== ''
          ) {
            setApiToken(
              result.access_token,
              'UserActionMenu.handleImpersonation',
            );

            window.location.replace(window.location.origin);
          }
        });
    },
    [getApiToken, setApiToken],
  );

  const onMenuClicked = ({ key }) => {
    // eslint-disable-next-line default-case
    switch (key) {
      case 'edit_user':
        onAction('editUser', user);
        break;
      case 'security_groups':
        onAction('securityGroups', user);
        break;
      case 'data_permissions':
        onAction('dataPermissions', user);
        break;
      case 'remove_ib_user':
        onAction('removeIBUser', user);
        break;
      case 'remove_user':
        onAction('removeUser', user);
        break;
      case 'view_profile':
      case 'view_details':
        navigate(`/dashboard/usermanagement/${user.id}`);
        break;
      case 'grant_access':
        onAction('activateUser', user);
        break;
      case 'impersonate':
        handleImpersonation(user);
        break;
    }
  };

  return (
    <>
      <Dropdown
        menu={{ items: getActionMenuItems(), onClick: onMenuClicked }}
        trigger={['click']}
      >
        <Button shape="circle" icon={<EllipsisOutlined />} />
      </Dropdown>
    </>
  );
};

export default UserActionMenu;
